import { BrowserRouter, Routes, Route, Navigate, NavLink as RouterNavLink, Outlet, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useState, useRef, useEffect } from 'react';
import { Truck, PlusCircle, Wrench, MessageCircle, Shield, UserPlus, Menu, X, LogIn, LogOut, ChevronDown, Bell, BarChart2, Briefcase, CreditCard, Users, Map, ClipboardList, Lightbulb, Star, CalendarDays, ShoppingCart, Tag, Newspaper, Package, Store, AlertTriangle } from 'lucide-react';
import { NotificationBell } from './components/NotificationBell';
import { useBazaarCartStore } from './stores/bazaarCartStore';
import { UserAvatar, type AvatarRole } from './components/UserAvatar';
import type { AppAccount } from './hooks/useAppAuth';
import { useAppAuth } from './hooks/useAppAuth';
import { visibleNavCards } from './lib/navCards';

// Pages
import { HomePage }        from './pages/HomePage';
import { PostJobPage }     from './pages/PostJobPage';
import { DashboardPage }   from './pages/DashboardPage';
import { ChatbotPage }     from './pages/ChatbotPage';
import { AdminReviewPage } from './pages/AdminReviewPage';
import { JobFeedPage } from './pages/JobFeedPage';
import { JobDetailPage } from './pages/JobDetailPage';

// New pages (plain default exports)
import RegistrationPage         from './pages/RegistrationPage';
import VoiceChatbotPage         from './pages/VoiceChatbotPage';
import UserDashboardPage        from './pages/UserDashboardPage';
import TrafficNewsDetailPage    from './pages/TrafficNewsDetailPage';
import MyJobsPage               from './pages/MyJobsPage';
import MyContractsPage          from './pages/MyContractsPage';
import MyDisputesPage           from './pages/MyDisputesPage';
import AdminUserManagementPage  from './pages/AdminUserManagementPage';
import EmailMonitoringPage      from './pages/EmailMonitoringPage';
import ProfilePage              from './pages/ProfilePage';
import PreferencesPage         from './pages/PreferencesPage';
import { PostRepairJobPage }   from './pages/PostRepairJobPage';
import DevLoginPage            from './pages/DevLoginPage';
import PrivacyPolicyPage       from './pages/PrivacyPolicyPage';
import TermsPage               from './pages/TermsPage';
import CookiePolicyPage                from './pages/CookiePolicyPage';
import HelpPage                       from './pages/HelpPage';
import AdminComplianceDocumentsPage    from './pages/AdminComplianceDocumentsPage';
import DocumentsPage                  from './pages/DocumentsPage';
import JobsBoardPage                   from './pages/JobsBoardPage';
import PaymentsBoardPage               from './pages/PaymentsBoardPage';
import ReferralsBoardPage              from './pages/ReferralsBoardPage';
import PaymentVerificationQueuePage    from './pages/PaymentVerificationQueuePage';
import AdminApprovalQueuePage          from './pages/AdminApprovalQueuePage';
import AdminPaymentReconciliationPage  from './pages/AdminPaymentReconciliationPage';
import CustomerPaymentsPage            from './pages/CustomerPaymentsPage';
import FleetMapPage                    from './pages/FleetMapPage';
import EmployeePortalPage                from './pages/EmployeePortalPage';
import JobMapPage                      from './pages/JobMapPage';
import EmployeesPage                   from './pages/EmployeesPage';
import VehiclesPage                    from './pages/VehiclesPage';
import PayoutSetupPage                 from './pages/PayoutSetupPage';
import SuggestionsPage                 from './pages/SuggestionsPage';
import AdminSuggestionsPage            from './pages/AdminSuggestionsPage';
import MyReviewsPage                   from './pages/MyReviewsPage';
import InvoicesPage                    from './pages/InvoicesPage';
import CreateInvoicePage               from './pages/CreateInvoicePage';
import InvoiceDetailPage               from './pages/InvoiceDetailPage';
import InvoiceSettingsPage             from './pages/InvoiceSettingsPage';
import AdminBazaarModerationPage       from './pages/AdminBazaarModerationPage';
import AdminPlatformFeesPage           from './pages/AdminPlatformFeesPage';
import WalletPage                      from './pages/WalletPage';
import AdminDisputesPage               from './pages/AdminDisputesPage';
import ExternalRatingUploadPage        from './pages/ExternalRatingUploadPage';
import AdminRatingReviewPage           from './pages/AdminRatingReviewPage';
import {
  PostAvailableLoadPage,
  AvailableLoadsBoardPage,
  AvailableLoadDetailPage,
  MyAvailableLoadsPage,
} from './pages/AvailableLoadsPage';
import {
  BazaarBrowsePage,
  ClassifiedsBrowsePage,
  BazaarListingDetailPage,
  ClassifiedListingDetailPage,
  CreateListingPage,
  MyBazaarPage,
  BazaarSellerShopPage,
} from './pages/BazaarPage';
import { BazaarCheckoutPage } from './pages/BazaarCheckoutPage';
import { ClassifiedCheckoutPage } from './pages/ClassifiedCheckoutPage';
import { BazaarBasketPage } from './pages/BazaarBasketPage';
import CalendarPage from './pages/CalendarPage';
import AutoQuoteSettingsPage from './pages/AutoQuoteSettingsPage';
import WorkshopServicesPage from './pages/WorkshopServicesPage';

// Legal / UI
import CookieConsent from './components/CookieConsent';
import Footer        from './components/Footer';

// Notifications
import NotificationsPage from './notifications/NotificationsPage';

// Chatbot widget
import { TransitDealChatbot } from './chatbot/TransitDealChatbot';
import { api, createApiClient, profileService, supportService } from './services/api';
import type { ChatbotMessageResponse } from './types';


const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 2, staleTime: 30_000 } },
});

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────
function displayNameFor(account: AppAccount): string {
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const given  = claims?.given_name  as string | undefined;
  const family = claims?.family_name as string | undefined;
  if (given && family) return `${given} ${family}`;
  if (given) return given;
  return account.name ?? account.username;
}

function toProperName(name: string): string {
  return name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

function roleFrom(account: AppAccount | undefined): string | undefined {
  const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
  return claims?.extension_Role as string | undefined;
}

// Kept for reference — role check now uses roleLower (includes profile fallback).
// Admin and Ops both get the admin nav items.

// ─────────────────────────────────────────────
//  DASHBOARDS DROPDOWN
// ─────────────────────────────────────────────
function DashboardsMenu({ onClose }: { onClose?: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const boards = [
    { to: '/dashboards/jobs',      icon: <Briefcase  className="w-4 h-4 text-blue-600" />,   label: 'Jobs Board',      desc: 'All jobs in one view' },
    { to: '/dashboards/payments',  icon: <CreditCard className="w-4 h-4 text-green-600" />,  label: 'Payments Board',  desc: 'Funds & payment history' },
    { to: '/dashboards/referrals', icon: <Users      className="w-4 h-4 text-purple-600" />, label: 'Referrals Board', desc: 'Network & earnings' },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors ${
          open ? 'text-blue-700 bg-blue-50 font-medium' : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
        }`}
      >
        <BarChart2 className="w-4 h-4" />
        Dashboards
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-50">
          {boards.map(b => (
            <RouterNavLink
              key={b.to}
              to={b.to}
              onClick={() => { setOpen(false); onClose?.(); }}
              className={({ isActive }) =>
                `flex items-start gap-3 px-4 py-3 transition-colors ${
                  isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`
              }
            >
              <span className="mt-0.5">{b.icon}</span>
              <span>
                <span className="block text-sm font-semibold text-gray-800">{b.label}</span>
                <span className="block text-xs text-gray-400">{b.desc}</span>
              </span>
            </RouterNavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// Mobile version — inline links
function DashboardsMenuMobile({ onClose }: { onClose: () => void }) {
  const [open, setOpen] = useState(false);

  const boards = [
    { to: '/dashboards/jobs',      icon: <Briefcase  className="w-4 h-4" />, label: 'Jobs Board' },
    { to: '/dashboards/payments',  icon: <CreditCard className="w-4 h-4" />, label: 'Payments Board' },
    { to: '/dashboards/referrals', icon: <Users      className="w-4 h-4" />, label: 'Referrals Board' },
  ];

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg text-gray-600 hover:bg-blue-50 transition-colors"
      >
        <BarChart2 className="w-4 h-4" />
        <span className="flex-1 text-left">Dashboards</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pl-4 flex flex-col gap-0.5">
          {boards.map(b => (
            <NavItem key={b.to} to={b.to} icon={b.icon} label={b.label} onClick={onClose} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  NAV BAR
// ─────────────────────────────────────────────
function NavBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);
  const { account, isAuthenticated, getToken } = useAppAuth();

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileService(createApiClient(getToken)).getProfile(),
    enabled: !!account,
  });

  const tokenRole         = roleFrom(account)?.toLowerCase();
  const roleLower         = tokenRole ?? profile?.role?.toLowerCase();
  const canBrowseJobs     = roleLower === 'carrier' || roleLower === 'workshop';
  const isAdminOrOps      = roleLower === 'admin' || roleLower === 'ops';
  const isCustomer        = roleLower === 'customer';
  const isCarrier         = roleLower === 'carrier';
  const isWorkshop        = roleLower === 'workshop';

  const navItems = [
    { to: '/bazaar',      icon: <Store     className="w-4 h-4" />, label: 'Bazaar'      },
    { to: '/classifieds', icon: <Newspaper className="w-4 h-4" />, label: 'Classifieds' },
    ...(!isAuthenticated ? [
      { to: '/register', icon: <UserPlus className="w-4 h-4" />, label: 'Register' },
    ] : [
      ...(canBrowseJobs ? [
        { to: '/jobs',        icon: <Truck        className="w-4 h-4" />, label: 'Jobs'         },
        { to: '/calendar',    icon: <CalendarDays className="w-4 h-4" />, label: 'Calendar'     },
      ] : []),
      { to: '/post-job',         icon: <PlusCircle   className="w-4 h-4" />, label: 'Post a Job'     },
      { to: '/available-loads',  icon: <Package      className="w-4 h-4" />, label: 'Available Loads' },
      ...(isCustomer ? [
        { to: '/payments', icon: <CreditCard className="w-4 h-4" />, label: 'Payments' },
      ] : []),
      ...(isCarrier ? [
        { to: '/fleet/map', icon: <Map className="w-4 h-4" />, label: 'Fleet Map' },
      ] : []),
    ]),
    ...(isAdminOrOps ? [
      { to: '/admin',                          icon: <Shield        className="w-4 h-4" />, label: 'Admin'       },
      { to: '/admin/approvals',                icon: <ClipboardList className="w-4 h-4" />, label: 'Approvals'   },
      { to: '/admin/payment-reconciliation',   icon: <CreditCard    className="w-4 h-4" />, label: 'Reconcile'   },
      { to: '/admin/suggestions',              icon: <Lightbulb     className="w-4 h-4" />, label: 'Suggestions' },
      { to: '/admin/platform-fees',            icon: <CreditCard    className="w-4 h-4" />, label: 'Platform Fees' },
      { to: '/admin/bazaar-moderation',        icon: <Store         className="w-4 h-4" />, label: 'Moderation'  },
      { to: '/admin/disputes',                 icon: <AlertTriangle className="w-4 h-4" />, label: 'Disputes'    },
      { to: '/admin/ratings',                  icon: <Star          className="w-4 h-4" />, label: 'Ratings'     },
    ] : []),
  ];

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
      <div className="w-full px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <RouterNavLink to="/" className="flex items-center gap-2 font-bold text-blue-600 text-lg shrink-0">
          <Truck className="w-6 h-6" />
          TransitDeal
        </RouterNavLink>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center gap-1">
          {navItems.map(item => (
            <NavItem key={item.to} {...item} />
          ))}
          {isAuthenticated && <DashboardsMenu />}
          {isAuthenticated && <BazaarBasketNavLink />}
          {isAuthenticated && <NotificationBell />}
          <AuthSection onClose={close} isCarrier={isCarrier} isWorkshop={isWorkshop} />
        </div>

        {/* Mobile: bell (always visible) + hamburger */}
        <div className="sm:hidden flex items-center gap-1">
          {isAuthenticated && <BazaarBasketNavLink />}
          {isAuthenticated && <NotificationBell />}
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="sm:hidden border-t border-gray-100 bg-white px-4 py-2 flex flex-col gap-1">
          {navItems.map(item => (
            <NavItem key={item.to} {...item} onClick={close} />
          ))}
          {isAuthenticated && <DashboardsMenuMobile onClose={close} />}
          <MobileAuthSection onClose={close} isCarrier={isCarrier} isWorkshop={isWorkshop} />
        </div>
      )}
    </nav>
  );
}

function BazaarBasketNavLink() {
  const count = useBazaarCartStore(s => s.totalItems());
  return (
    <RouterNavLink
      to="/bazaar/basket"
      className="relative p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
      aria-label="Bazaar basket"
    >
      <ShoppingCart className="w-5 h-5" />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-green-600 text-white text-[10px] font-bold">
          {count}
        </span>
      )}
    </RouterNavLink>
  );
}

function NavItem({ to, icon, label, end, onClick }: { to: string; icon: React.ReactNode; label: string; end?: boolean; onClick?: () => void }) {
  return (
    <RouterNavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors ${
          isActive
            ? 'text-blue-700 bg-blue-50 font-medium'
            : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
        }`
      }
    >
      {icon}{label}
    </RouterNavLink>
  );
}

// ─────────────────────────────────────────────
//  AUTH SECTION (desktop)
// ─────────────────────────────────────────────
function AuthSection({ onClose, isCarrier, isWorkshop }: { onClose: () => void; isCarrier: boolean; isWorkshop: boolean }) {
  const { account, getToken, login, logout } = useAppAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: userSummary } = useQuery({
    queryKey: ['user-summary'],
    queryFn: () =>
      createApiClient(getToken)
        .get<{ fullName: string }>('/api/v1/users/me')
        .then(r => r.data),
    enabled: !!account,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!account) {
    return (
      <button
        onClick={() => login()}
        className="ml-2 flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
      >
        <LogIn className="w-4 h-4" /> Sign In
      </button>
    );
  }

  const displayName = toProperName(userSummary?.fullName ?? displayNameFor(account));

  return (
    <div ref={ref} className="relative ml-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <UserAvatar
          name={displayName}
          role={roleFrom(account)?.replace(/^\w/, c => c.toUpperCase()) as AvatarRole | undefined}
          size="xs"
        />
        <span className="max-w-[100px] truncate">{displayName}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-50">
          {visibleNavCards({ isCarrier, isWorkshop }).map(card => (
            <RouterNavLink
              key={card.to}
              to={card.to}
              onClick={() => { setOpen(false); onClose(); }}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <card.icon className="w-4 h-4 text-gray-400" /> {card.label}
            </RouterNavLink>
          ))}
          <div className="border-t border-gray-100 my-1" />
          <button
            onClick={() => { setOpen(false); logout(); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  AUTH SECTION (mobile)
// ─────────────────────────────────────────────
function MobileAuthSection({ onClose, isCarrier, isWorkshop }: { onClose: () => void; isCarrier: boolean; isWorkshop: boolean }) {
  const { account, getToken, login, logout } = useAppAuth();

  const { data: userSummary } = useQuery({
    queryKey: ['user-summary'],
    queryFn: () =>
      createApiClient(getToken)
        .get<{ fullName: string }>('/api/v1/users/me')
        .then(r => r.data),
    enabled: !!account,
  });

  if (!account) {
    return (
      <button
        onClick={() => { onClose(); login(); }}
        className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors font-medium mt-1"
      >
        <LogIn className="w-4 h-4" /> Sign In
      </button>
    );
  }

  const displayName = userSummary?.fullName ?? displayNameFor(account);

  return (
    <>
      <div className="px-3 py-2 flex items-center gap-2 mt-1">
        <UserAvatar
          name={displayName}
          role={roleFrom(account)?.replace(/^\w/, c => c.toUpperCase()) as AvatarRole | undefined}
          size="sm"
        />
        <span className="text-xs font-semibold text-gray-600 truncate">{displayName}</span>
      </div>
      <NavItem to="/notifications"  icon={<Bell     className="w-4 h-4" />} label="Notifications"    onClick={onClose} />
      {visibleNavCards({ isCarrier, isWorkshop }).map(card => (
        <NavItem key={card.to} to={card.to} icon={<card.icon className="w-4 h-4" />} label={card.label} onClick={onClose} />
      ))}
      <button
        onClick={() => { onClose(); logout(); }}
        className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg text-red-600 hover:bg-red-50 transition-colors"
      >
        <LogOut className="w-4 h-4" /> Sign Out
      </button>
    </>
  );
}

// ─────────────────────────────────────────────
//  FLOATING CHATBOT WIDGET
// ─────────────────────────────────────────────
function ChatbotWidget() {
  const navigate = useNavigate();
  const [open, setOpen]           = useState(false);
  const [tab, setTab]             = useState<'ai' | 'support'>('ai');
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [subject, setSubject]     = useState('');
  const [message, setMessage]     = useState('');
  const [sending, setSending]     = useState(false);
  const [sent, setSent]           = useState(false);

  const sendMessage = async (msg: string, sid?: string): Promise<ChatbotMessageResponse> => {
    const r = await api.post<ChatbotMessageResponse>('/chatbot/message', { message: msg, sessionId: sid ?? sessionId });
    if (!sessionId && r.data.sessionId) setSessionId(r.data.sessionId);
    return r.data;
  };

  const submitSupport = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSending(true);
    try {
      await supportService(api).submitRequest(subject.trim(), message.trim());
      setSent(true);
      setSubject('');
      setMessage('');
    } catch {
      // fall through — user sees the error inline
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:w-96 z-50 shadow-2xl rounded-2xl overflow-hidden flex flex-col bg-white">
          {/* Tab bar */}
          <div className="flex border-b border-gray-100">
            {(['ai', 'support'] as const).map(t => (
              <button
                key={t}
                onClick={() => { setTab(t); setSent(false); }}
                className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
                  tab === t ? 'text-blue-700 border-b-2 border-blue-700 -mb-px' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {t === 'ai' ? '✦ AI Assistant' : '✉ Contact Support'}
              </button>
            ))}
          </div>

          {/* AI tab */}
          {tab === 'ai' && (
            <TransitDealChatbot
              sendMessage={sendMessage}
              initialGreeting="Hi! I can help you post a job, complete your driver profile, or answer questions about TransitDeal. What would you like to do?"
              onNavigate={(path) => { setOpen(false); navigate(path); }}
            />
          )}

          {/* Support tab */}
          {tab === 'support' && (
            <div className="p-4 flex flex-col gap-3">
              {sent ? (
                <div className="text-center py-8 flex flex-col items-center gap-2">
                  <span className="text-3xl">✅</span>
                  <p className="text-sm font-semibold text-gray-800">Request received!</p>
                  <p className="text-xs text-gray-500">We'll get back to you shortly.</p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-2 text-xs text-blue-600 underline"
                  >Send another</button>
                </div>
              ) : (
                <form onSubmit={submitSupport} className="flex flex-col gap-3">
                  <p className="text-xs text-gray-500">
                    Send us a message or email <a href="mailto:support@transitdeal.com" className="text-blue-600">support@transitdeal.com</a>
                  </p>
                  <input
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder="Subject"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    required
                  />
                  <textarea
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder="Describe your issue…"
                    rows={5}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    required
                  />
                  <button
                    type="submit"
                    disabled={sending || !subject.trim() || !message.trim()}
                    className="bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
                  >
                    {sending ? 'Sending…' : 'Send to Support Team'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-4 right-4 w-14 h-14 bg-blue-600 hover:bg-blue-700
                   text-white rounded-full shadow-lg flex items-center justify-center
                   z-50 transition-colors"
        aria-label="Open assistant"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </>
  );
}

// ─────────────────────────────────────────────
//  LAYOUT WRAPPERS
// ─────────────────────────────────────────────

// Standard pages: NavBar + full-width main (gutters only) + Footer + chatbot widgets.
// Pages own their vertical rhythm via .page-shell / .page-shell-tight; none of them cap width.
function StandardLayout() {
  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <main className="w-full px-4 sm:px-6 lg:px-8">
        <Outlet />
      </main>
      <Footer />
      <ChatbotWidget />
      <CookieConsent />
    </div>
  );
}

// Edge-to-edge pages (e.g. Fleet Map): NavBar only - no gutters, no footer widgets
function FullWidthLayout() {
  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <Outlet />
    </div>
  );
}

// ─────────────────────────────────────────────
//  HOME OR DASHBOARD
//  Unauthenticated → marketing home page
//  Authenticated   → user dashboard
// ─────────────────────────────────────────────
function HomeOrDashboard() {
  const { isAuthenticated } = useAppAuth();
  if (isAuthenticated) return <Navigate to="/my-dashboard" replace />;
  return <HomePage />;
}

// ─────────────────────────────────────────────
//  APP ROOT
// ─────────────────────────────────────────────
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Driver portal — completely standalone, no nav/footer/chatbot */}
          <Route path="/driver/:token" element={<EmployeePortalPage />} />

          {/* Edge-to-edge layout — NavBar only, page paints to the viewport edge */}
          <Route element={<FullWidthLayout />}>
            <Route path="/fleet/map"              element={<FleetMapPage />} />
            <Route path="/jobs"                   element={<JobFeedPage />}  />
            <Route path="/jobs/map"               element={<JobMapPage />} />
            <Route path="/available-loads"        element={<AvailableLoadsBoardPage />} />
          </Route>

          {/* Standard layout — NavBar + full-width main + Footer + chatbot */}
          <Route element={<StandardLayout />}>
            <Route path="/"             element={<HomeOrDashboard />} />
            <Route path="/jobs/:id"     element={<JobDetailPage />}   />
            <Route path="/post-job"                    element={<PostJobPage />}              />
            <Route path="/available-loads/post"    element={<PostAvailableLoadPage />}    />
            <Route path="/available-loads/mine"    element={<MyAvailableLoadsPage />}     />
            <Route path="/available-loads/:id"     element={<AvailableLoadDetailPage />}  />
            <Route path="/post-repair"  element={<PostRepairJobPage />}  />
            <Route path="/dashboard"    element={<DashboardPage />}   />
            <Route path="/assistant"    element={<ChatbotPage />}     />
            <Route path="/admin/review" element={<AdminReviewPage />} />
            <Route path="/register"     element={<RegistrationPage />}        />
            <Route path="/chatbot"      element={<VoiceChatbotPage />}        />
            <Route path="/my-dashboard" element={<UserDashboardPage />}       />
            <Route path="/traffic-news/:id" element={<TrafficNewsDetailPage />} />
            <Route path="/my-jobs"           element={<MyJobsPage />}              />
            <Route path="/calendar"         element={<CalendarPage />}            />
            <Route path="/auto-quote"       element={<AutoQuoteSettingsPage />}   />
            <Route path="/workshop-services" element={<WorkshopServicesPage />}   />
            <Route path="/classifieds"      element={<ClassifiedsBrowsePage />}       />
            <Route path="/classified/:id"   element={<ClassifiedListingDetailPage />} />
            <Route path="/bazaar"           element={<BazaarBrowsePage />}            />
            <Route path="/bazaar/create"    element={<CreateListingPage />}           />
            <Route path="/bazaar/mine"      element={<MyBazaarPage />}                />
            <Route path="/bazaar/basket"    element={<BazaarBasketPage />}            />
            <Route path="/bazaar/seller/:sellerId" element={<BazaarSellerShopPage />} />
            <Route path="/bazaar/checkout/:orderId" element={<BazaarCheckoutPage />}  />
            <Route path="/classifieds/checkout/:listingId" element={<ClassifiedCheckoutPage />} />
            <Route path="/bazaar/:id"       element={<BazaarListingDetailPage />}     />
            <Route path="/buysell"          element={<Navigate to="/bazaar" replace />} />
            <Route path="/buy"              element={<Navigate to="/bazaar" replace />} />
            <Route path="/sell"             element={<Navigate to="/bazaar/create?type=sell" replace />} />
            <Route path="/my-contracts" element={<MyContractsPage />}         />
            <Route path="/my-disputes"  element={<MyDisputesPage />}          />
            <Route path="/profile"      element={<ProfilePage />}             />
            <Route path="/preferences"  element={<PreferencesPage />}         />
            <Route path="/documents"    element={<DocumentsPage />} />
            <Route path="/notifications" element={<NotificationsPage />}      />
            <Route path="/job-alerts"   element={<Navigate to="/preferences" replace />} />
            <Route path="/employees"    element={<EmployeesPage />} />
            <Route path="/vehicles"     element={<VehiclesPage />} />
            <Route path="/payouts"      element={<PayoutSetupPage />} />
            <Route path="/suggestions"  element={<SuggestionsPage />} />
            <Route path="/my-reviews"   element={<MyReviewsPage />} />
            <Route path="/my-ratings"   element={<ExternalRatingUploadPage />} />
            <Route path="/invoices"          element={<InvoicesPage />}         />
            <Route path="/wallet"            element={<WalletPage />}           />
            <Route path="/invoices/create"   element={<CreateInvoicePage />}    />
            <Route path="/invoices/settings" element={<InvoiceSettingsPage />}  />
            <Route path="/invoices/:id"      element={<InvoiceDetailPage />}    />
            <Route path="/admin"                       element={<AdminUserManagementPage />}      />
            <Route path="/admin/suggestions"           element={<AdminSuggestionsPage />}          />
            <Route path="/admin/users"                 element={<AdminUserManagementPage />}      />
            <Route path="/admin/users/:userId"         element={<AdminUserManagementPage />}      />
            <Route path="/admin/mailbox"               element={<EmailMonitoringPage />}          />
            <Route path="/admin/documents"             element={<AdminComplianceDocumentsPage />} />
            <Route path="/admin/payment-verification"  element={<PaymentVerificationQueuePage />}    />
            <Route path="/admin/payment-reconciliation" element={<AdminPaymentReconciliationPage />} />
            <Route path="/admin/approvals"             element={<AdminApprovalQueuePage />}          />
            <Route path="/admin/platform-fees"         element={<AdminPlatformFeesPage />}          />
            <Route path="/admin/bazaar-moderation"     element={<AdminBazaarModerationPage />}      />
            <Route path="/admin/disputes"              element={<AdminDisputesPage />}              />
            <Route path="/admin/ratings"               element={<AdminRatingReviewPage />}          />
            <Route path="/payments"                    element={<CustomerPaymentsPage />}         />
            <Route path="/dashboards/jobs"             element={<JobsBoardPage />}                />
            <Route path="/dashboards/payments"         element={<PaymentsBoardPage />}            />
            <Route path="/dashboards/referrals"        element={<ReferralsBoardPage />}           />
            {import.meta.env.DEV && <Route path="/dev-login" element={<DevLoginPage />} />}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms"   element={<TermsPage />}         />
            <Route path="/cookies" element={<CookiePolicyPage />}  />
            <Route path="/help"    element={<HelpPage />}          />
            <Route path="*"        element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
